import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarClock, FileDown, Printer, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const DAYS = [{ day: 1, label: "Segunda" }, { day: 2, label: "Terça" }, { day: 3, label: "Quarta" }, { day: 4, label: "Quinta" }, { day: 5, label: "Sexta" }];

function TeacherGrid({ data }) {
  const navigate = useNavigate();
  const at = (d, r) => data.cells.filter((c) => c.day === d && c.start === r.start && c.end === r.end);
  return (
    <div className="overflow-x-auto" data-testid="teacher-schedule-grid">
      <table className="w-full min-w-[860px] border-separate border-spacing-1 text-sm">
        <thead>
          <tr>
            <th className="w-28 rounded-md bg-slate-800 px-2 py-2 text-left text-xs font-semibold text-white">Horário</th>
            {DAYS.map((d) => <th key={d.day} className="rounded-md bg-slate-800 px-2 py-2 text-xs font-semibold text-white">{d.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={`${r.start}-${r.end}`}>
              <td className="rounded-md bg-slate-100 px-2 py-2 align-top text-xs font-semibold text-slate-700">{r.start} – {r.end}</td>
              {DAYS.map((d) => {
                const items = at(d.day, r);
                return (
                  <td key={d.day} className="align-top">
                    {items.length ? items.map((c) => (
                      <button key={c.class_id} type="button" onClick={() => navigate(`/quadro-horarios?turma=${c.class_id}`)}
                        data-testid={`teacher-cell-${d.day}-${r.start}-${c.class_id}`}
                        className={`mb-1 w-full rounded-md border p-2 text-left transition-colors ${items.length > 1 ? "border-red-300 bg-red-50 hover:bg-red-100" : "border-orange-200 bg-orange-50 hover:bg-orange-100"}`}>
                        <div className="font-semibold leading-tight text-slate-900">Turma {c.class_name}</div>
                        <div className="text-xs text-slate-600">{c.subject_name || "Sem disciplina"}</div>
                        <div className="text-[10px] text-slate-400">{c.period}º tempo • {c.shift}</div>
                      </button>
                    )) : <div className="min-h-[60px] rounded-md border border-dashed border-slate-200" />}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function HorarioProfessor() {
  const { user, selectedSchoolId } = useAuth();
  const [teacherId, setTeacherId] = useState("");
  const [data, setData] = useState(null);
  const [pdfBusy, setPdfBusy] = useState("");
  const isProf = user?.role === "PROFESSOR";

  const getPdf = async (mode) => {
    setPdfBusy(mode);
    try {
      const r = await api.get("/schedule/teacher/pdf", { params: { teacher_id: teacherId || undefined, school_id: selectedSchoolId || undefined }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      if (mode === "print") {
        const w = window.open(url, "_blank");
        if (!w) toast.error("Permita pop-ups para imprimir");
        else w.addEventListener("load", () => w.print());
      } else {
        const a = document.createElement("a");
        a.href = url; a.download = `horario_${(data?.teacher?.name || "professor").replace(/\s+/g, "_")}.pdf`; a.click();
      }
    } catch { toast.error("Falha ao gerar o PDF"); } finally { setPdfBusy(""); }
  };

  const load = useCallback(() => {
    setData(null);
    api.get("/schedule/teacher", { params: { teacher_id: teacherId || undefined, school_id: selectedSchoolId || undefined } })
      .then((r) => { setData(r.data); if (!teacherId && r.data.teacher) setTeacherId(r.data.teacher.id); })
      .catch(() => setData({ teacher: null, teachers: [], rows: [], cells: [], total: 0 }));
  }, [teacherId, selectedSchoolId]);
  useEffect(() => { load(); }, [load]);

  const classes = data ? [...new Set(data.cells.map((c) => c.class_name))] : [];
  const conflicts = data ? data.cells.filter((c, i, a) => a.some((o, j) => j !== i && o.day === c.day && o.start === c.start)).length : 0;
  return (
    <div data-testid="horario-professor-page">
      <PageHeader title={isProf ? "Meu Horário" : "Horário do Professor"} subtitle="Grade semanal com todas as turmas e tempos em que o professor leciona, montada a partir do Quadro de Horários.">
        {data?.total > 0 && (
          <>
            <Button variant="outline" onClick={() => getPdf("print")} disabled={!!pdfBusy} data-testid="teacher-pdf-print-btn">
              {pdfBusy === "print" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Printer className="mr-2 h-4 w-4" />}Imprimir
            </Button>
            <Button onClick={() => getPdf("download")} disabled={!!pdfBusy} className="bg-orange-500 hover:bg-orange-600" data-testid="teacher-pdf-download-btn">
              {pdfBusy === "download" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}Baixar PDF
            </Button>
          </>
        )}
      </PageHeader>
      {!isProf && data && (
        <Card className="mb-4 p-4">
          <Label>Professor</Label>
          <Select value={teacherId} onValueChange={setTeacherId}>
            <SelectTrigger className="mt-1.5 md:max-w-md" data-testid="teacher-select"><SelectValue placeholder="Selecione o professor" /></SelectTrigger>
            <SelectContent>{data.teachers.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`teacher-option-${t.id}`}>{t.name}{t.registration ? ` • ${t.registration}` : ""}</SelectItem>)}</SelectContent>
          </Select>
        </Card>
      )}
      {!data ? <p className="py-10 text-center text-sm text-slate-400">Carregando...</p>
        : !data.teacher ? <EmptyState icon={CalendarClock} title={isProf ? "Seu usuário não está vinculado a um professor" : "Nenhum professor cadastrado"} description={isProf ? "Peça à direção para vincular seu usuário ao cadastro de Professores." : "Cadastre professores para ver o horário."} />
        : !data.total ? <EmptyState icon={CalendarClock} title="Nenhum tempo no quadro" description={`${data.teacher.name} ainda não aparece no Quadro de Horários de nenhuma turma.`} />
        : (
          <Card className="min-w-0 p-3 sm:p-4">
            <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
              <Badge className="bg-slate-800" data-testid="teacher-name-badge">{data.teacher.name}</Badge>
              {data.teacher.registration && <Badge variant="outline">ID {data.teacher.registration}</Badge>}
              <Badge className="bg-orange-500" data-testid="teacher-total-badge">{data.total} tempo(s) por semana</Badge>
              <Badge variant="outline" data-testid="teacher-classes-badge">Turmas: {classes.join(", ")}</Badge>
              {conflicts > 0 && <Badge className="bg-red-600" data-testid="teacher-conflict-badge">Conflito de horário em {conflicts} tempo(s)</Badge>}
            </div>
            <TeacherGrid data={data} />
            <p className="mt-3 text-xs text-slate-500">Clique em uma aula para abrir o quadro de horários da turma. Tempos em vermelho indicam duas turmas no mesmo horário.</p>
          </Card>
        )}
    </div>
  );
}
